/**
 * Background Renderer
 *
 * Renders the background layer (solid color + line art + grain) as a
 * static image that sits behind the Fabric.js interactive canvas.
 * This gets regenerated when the scheme, line art, or grain settings change,
 * but NOT on every object drag/move (which keeps interaction smooth).
 */

import { drawLineArt } from '../overlays/line-art.js';
import { applyGrain } from '../overlays/grain.js';
import { normalizeOverlay } from '../utils/normalize.js';

/**
 * Render the background (color + overlays) to an offscreen canvas
 * and return it as a data URL for use as a Fabric.js background image.
 *
 * @param {object} params
 * @param {number} params.width
 * @param {number} params.height
 * @param {object} params.scheme - color scheme object
 * @param {object} params.overlays - { lineArt: {...}, grain: {...} }
 * @returns {string} data URL of the rendered background
 */
export function renderBackground({ width, height, scheme, overlays, watermark = true, returnCanvas = false }) {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');

  // Solid background
  ctx.fillStyle = scheme.bg;
  ctx.fillRect(0, 0, width, height);

  // Primary line art overlay
  if (overlays.lineArt && overlays.lineArt.type !== 'none') {
    const la = overlays.lineArt;
    drawLineArt(
      ctx, width, height,
      la.type,
      normalizeOverlay(la.density, 50),
      normalizeOverlay(la.opacity, 30),
      scheme.lineArt || '#333333',
      la.seed || 42
    );
  }

  // Secondary line art overlay — uses scheme's lineArt2 color
  if (overlays.lineArt2 && overlays.lineArt2.type !== 'none') {
    const la2 = overlays.lineArt2;
    drawLineArt(
      ctx, width, height,
      la2.type,
      normalizeOverlay(la2.density, 30),
      normalizeOverlay(la2.opacity, 20),
      scheme.lineArt2 || scheme.accent || '#666666',
      la2.seed || 137
    );
  }

  // Grain overlay
  if (overlays.grain && overlays.grain.enabled) {
    const g = overlays.grain;
    applyGrain(
      ctx, width, height, scheme.isDark,
      normalizeOverlay(g.density, 40),
      normalizeOverlay(g.opacity, 15)
    );
  }

  // Diagonal watermark — covers entire surface, can't be cropped off
  if (watermark !== false) {
    drawDiagonalWatermark(ctx, width, height, scheme.isDark);
  }

  if (returnCanvas) return canvas;
  return canvas.toDataURL('image/png');
}

/**
 * Render a diagonal repeating "stencil.cloud" watermark across the full canvas.
 * Rotated at -25°, tiled in a grid — cropping any region still shows the mark.
 */
function drawDiagonalWatermark(ctx, w, h, isDark) {
  ctx.save();

  const text = 'stencil.cloud';
  const fontSize = Math.max(14, Math.round(Math.min(w, h) * 0.028));
  ctx.font = `600 ${fontSize}px "DM Mono", monospace`;
  ctx.fillStyle = isDark ? 'rgba(255,255,255,0.045)' : 'rgba(0,0,0,0.04)';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  const angle = -25 * (Math.PI / 180);
  const spacingX = fontSize * text.length * 0.65;
  const spacingY = fontSize * 3.5;

  // Translate to center, rotate, then tile from well beyond the edges
  // so rotation doesn't leave gaps in corners
  ctx.translate(w / 2, h / 2);
  ctx.rotate(angle);

  const diagonal = Math.sqrt(w * w + h * h);
  const cols = Math.ceil(diagonal / spacingX) + 2;
  const rows = Math.ceil(diagonal / spacingY) + 2;

  for (let row = -rows; row <= rows; row++) {
    for (let col = -cols; col <= cols; col++) {
      const x = col * spacingX;
      const y = row * spacingY;
      ctx.fillText(text, x, y);
    }
  }

  ctx.restore();
}
